/**
 * User Role Types and Utilities
 *
 * Defines all user roles in the Bono ecosystem and provides
 * utilities for role categorization and management.
 */

/**
 * All possible user roles in the system
 */
export enum UserRole {
  // Carbon Accounting Roles
  CARBON_ACCOUNTING_USER = 'CARBON_ACCOUNTING_USER',
  CARBON_ACCOUNTING_ADMIN = 'CARBON_ACCOUNTING_ADMIN',
  CARBON_ACCOUNTING_READ_ONLY = 'CARBON_ACCOUNTING_READ_ONLY',

  // Supply Chain Roles
  SUPPLY_CHAIN_USER = 'SUPPLY_CHAIN_USER',
  SUPPLY_CHAIN_ADMIN = 'SUPPLY_CHAIN_ADMIN',
  SUPPLY_CHAIN_READ_ONLY = 'SUPPLY_CHAIN_READ_ONLY',

  // Vendor Roles
  VENDOR_USER = 'VENDOR_USER',
  VENDOR_ADMIN = 'VENDOR_ADMIN',
  VENDOR_READ_ONLY = 'VENDOR_READ_ONLY',

  // Partner Roles
  PARTNER_USER = 'PARTNER_USER',
  PARTNER_ADMIN = 'PARTNER_ADMIN',
  PARTNER_READ_ONLY = 'PARTNER_READ_ONLY',

  // Bono Internal Roles
  BONO_ACCOUNT_MANAGER = 'BONO_ACCOUNT_MANAGER',
  BONO_SUPER_ADMIN = 'BONO_SUPER_ADMIN',

  // ESG Roles
  ESG_ADMIN = 'ESG_ADMIN',
  ESG_FACILITY_RESPONSIBLE = 'ESG_FACILITY_RESPONSIBLE',
  ESG_DATA_CONTRIBUTOR = 'ESG_DATA_CONTRIBUTOR',
  ESG_REVIEWER = 'ESG_REVIEWER',
  ESG_READ_ONLY = 'ESG_READ_ONLY',
}

/**
 * Role categories for grouping related roles
 */
export enum RoleCategory {
  BONO_INTERNAL = 'BONO_INTERNAL',
  PARTNER = 'PARTNER',
  CARBON_ACCOUNTING = 'CARBON_ACCOUNTING',
  SUPPLY_CHAIN = 'SUPPLY_CHAIN',
  VENDOR = 'VENDOR',
  ESG = 'ESG',
}

/**
 * Role group type (array of UserRole strings)
 * This matches the format from Cognito JWT tokens
 */
export type RoleGroup = string[];

/**
 * Role priority for determining which app to redirect to
 * when a user has multiple roles
 */
export interface RolePriority {
  role: UserRole;
  priority: number;
  category: RoleCategory;
}

/**
 * Role permission structure (for future use)
 */
export interface RolePermission {
  role: UserRole;
  permissions: string[];
  description: string;
}

/**
 * Helper type for role checking
 */
export type RoleCheck = (roles: UserRole[]) => boolean;

/**
 * Type guard to check if a string is a valid UserRole
 */
export function isValidUserRole(role: string): role is UserRole {
  return Object.values(UserRole).includes(role as UserRole);
}

/**
 * Convert role group from JWT to UserRole array
 */
export function parseRoleGroup(roleGroup: RoleGroup): UserRole[] {
  return roleGroup
    .filter(isValidUserRole)
    .map(role => role as UserRole);
}

/**
 * Check if user has any of the specified roles
 */
export function hasAnyRole(userRoles: UserRole[], requiredRoles: UserRole[]): boolean {
  return requiredRoles.some(role => userRoles.includes(role));
}

/**
 * Check if user has all of the specified roles
 */
export function hasAllRoles(userRoles: UserRole[], requiredRoles: UserRole[]): boolean {
  return requiredRoles.every(role => userRoles.includes(role));
}

/**
 * Get role category
 */
export function getRoleCategory(role: UserRole): RoleCategory {
  if (role === UserRole.BONO_SUPER_ADMIN || role === UserRole.BONO_ACCOUNT_MANAGER) {
    return RoleCategory.BONO_INTERNAL;
  }
  if (role.startsWith('PARTNER_')) {
    return RoleCategory.PARTNER;
  }
  if (role.startsWith('CARBON_ACCOUNTING_')) {
    return RoleCategory.CARBON_ACCOUNTING;
  }
  if (role.startsWith('SUPPLY_CHAIN_')) {
    return RoleCategory.SUPPLY_CHAIN;
  }
  if (role.startsWith('VENDOR_')) {
    return RoleCategory.VENDOR;
  }
  if (role.startsWith('ESG_')) {
    return RoleCategory.ESG;
  }
  return RoleCategory.CARBON_ACCOUNTING; // Default fallback
}